import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PaymentRoutes } from "../../../../Contracts/Routes";
import { useOnboardingStore } from "../../../../Store/Onboarding";
import { useUserStore } from "../../../../Store/User";
import { useSubscriptionStore } from "../../../../Store/Subscription";
import { lazyWithRetry } from "../../../../Utils/LazyLoadUtils";
import LoadingPage from "../../../../Pages/LoadingPage";

const AccountDeactivatedModal = lazyWithRetry(
  () => import("../../../../Components/Molecules/AccountDeactivatedModal")
);

const GettingStarted = lazyWithRetry(() => import("../../../../Pages/GettingStarted"));
const InvoiceListPage = lazyWithRetry(() => import("../../../../Pages/InvoiceListPage"));
const PayoutListPage = lazyWithRetry(() => import("../../../../Pages/PayoutListPage"));
const CustomerListPage = lazyWithRetry(() => import("../../../../Pages/CustomerListPage"));
const TransactionListPage = lazyWithRetry(() => import("../../../../Pages/TransactionListPage"));
const MyConfigurationPage = lazyWithRetry(
  () => import("../../../../Pages/SettingsPages/MyConfigurationPage")
);
const PaymentsIntegrationPage = lazyWithRetry(
  () => import("../../../../Pages/SettingsPages/PaymentsIntegrationPage")
);
const CustomerDetailPage = lazyWithRetry(() => import("../../../../Pages/CustomerDetailPage"));
const ClientPortalLoginPage = lazyWithRetry(() => import("../../../../Pages/ClientPortalLoginPage"));
const ClientPortalInvoiceListPage = lazyWithRetry(
  () => import("../../../../Pages/ClientPortalInvoiceListPage")
);
const ClientPortalCustomerListPage = lazyWithRetry(
  () => import("../../../../Pages/ClientPortalCustomerListPage")
);
const UserManagementPage = lazyWithRetry(
  () => import("../../../../Pages/SettingsPages/UserManagementPage")
);
const OnboardingStartForm = lazyWithRetry(() => import("../../../../Pages/OnboardingStartForm"));
const RegisterPage = lazyWithRetry(() => import("../../../../Pages/RegisterPage"));
const ZomentumGrowQuotePayPage = lazyWithRetry(
  () => import("../../../../Pages/ZomentumGrowQuotePayPage")
);
const QuickbooksSettingsPage = lazyWithRetry(
  () => import("../../../../Pages/SettingsPages/PaymentsIntegrationPage/QuickbooksSettingsPage")
);
const XeroSettingsPage = lazyWithRetry(
  () => import("../../../../Pages/SettingsPages/PaymentsIntegrationPage/XeroSettingsPage")
);
const ClientPortalCustomerDetailPage = lazyWithRetry(
  () => import("../../../../Pages/ClientPortalCustomerDetailPage")
);
const AddPaymentMethodMandatePage = lazyWithRetry(
  () => import("../../../../Pages/AddPaymentMethodMandatePage")
);
const AdminTeamListPage = lazyWithRetry(() => import("../../../../Pages/AdminTeamListPage"));
const CheckoutInvoicePage = lazyWithRetry(() => import("../../../../Pages/CheckoutInvoicePage"));
const InvoiceReceiptPage = lazyWithRetry(() => import("../../../../Pages/InvoiceReceiptPage"));
const AdminTeamDetailPage = lazyWithRetry(() => import("../../../../Pages/AdminTeamDetailPage"));
const AdminTeamTransactionsListPage = lazyWithRetry(
  () => import("../../../../Pages/AdminTeamTransactionsListPage")
);
const AdminTeamTransfersListPage = lazyWithRetry(
  () => import("../../../../Pages/AdminTeamTransfersListPage")
);
const UpcomingPayoutsPage = lazyWithRetry(() => import("../../../../Pages/UpcomingPayoutsPage"));
const IncomingTransactionsPage = lazyWithRetry(
  () => import("../../../../Pages/IncomingTransactionsPage")
);
const PayoutDetailPage = lazyWithRetry(() => import("../../../../Pages/PayoutDetailPage"));
const PayoutTroubleshootPage = lazyWithRetry(
  () => import("../../../../Pages/PayoutTroubleshootPage")
);
const InternalServerErrorPage = lazyWithRetry(
  () => import("../../../../Pages/ErrorPage/InternalServerErrorPage")
);
const SessionEndedPage = lazyWithRetry(() => import("../../../../Pages/ErrorPage/SessionEndedPage"));
const AccountCancelledPage = lazyWithRetry(
  () => import("../../../../Pages/ErrorPage/AccountCancelledPage")
);
const InvoiceAutoCollectDetailsPage = lazyWithRetry(
  () => import("../../../../Pages/InvoiceAutoCollectDetailsPage")
);
const AcceptUserInvitePage = lazyWithRetry(() => import("../../../../Pages/AcceptUserInvitePage"));
const AdminTeamUserListPage = lazyWithRetry(() => import("../../../../Pages/AdminTeamUserListPage"));
const AdminTeamPayoutsListPage = lazyWithRetry(
  () => import("../../../../Pages/AdminTeamPayoutsListPage")
);
const AdminTeamPayoutTroubleshootPage = lazyWithRetry(
  () => import("../../../../Pages/AdminTeamPayoutTroubleshootPage")
);
const RefundReceiptPage = lazyWithRetry(() => import("../../../../Pages/TemplateReceiptPage"));
const PaymentLinksListPage = lazyWithRetry(() => import("../../../../Pages/PaymentLinksListPage"));
const PaymentLinkCheckoutPage = lazyWithRetry(
  () => import("../../../../Pages/PaymentLinkCheckoutPage")
);

const PaymentDashboardRoutes: React.FC = () => {
  const { isOnboardingComplete } = useOnboardingStore();
  const { currentUser } = useUserStore();
  const isAccountDeactivated = useSubscriptionStore(s => s.isAccountDeactivated);
  const onboardingComplete = isOnboardingComplete();

  return (
    <>
      {isAccountDeactivated() && (
        <Suspense fallback={null}>
          <AccountDeactivatedModal />
        </Suspense>
      )}
      <Suspense fallback={<div />}>
      <Routes>
        <Route path={PaymentRoutes.GETTING_STARTED} element={<GettingStarted />} />
        <Route path={PaymentRoutes.LOADING} element={<LoadingPage />} />
        <Route
          path={PaymentRoutes.INTERNAL_SERVER_ERROR}
          element={<InternalServerErrorPage />}
        />
        <Route path={PaymentRoutes.SESSION_ENDED} element={<SessionEndedPage />} />
        <Route path={PaymentRoutes.ACCOUNT_CANCELLED} element={<AccountCancelledPage />} />
        {onboardingComplete && (
          <>
            <Route path={PaymentRoutes.CUSTOMERS} element={<CustomerListPage />} />
            <Route path={PaymentRoutes.INVOICES} element={<InvoiceListPage />} />
          </>
        )}
        <Route path={PaymentRoutes.CUSTOMERS_DETAILS} element={<CustomerDetailPage />} />
        <Route path={PaymentRoutes.PAYOUTS} element={<PayoutListPage />} />
        <Route path={PaymentRoutes.PAYOUT_UPCOMING} element={<UpcomingPayoutsPage />} />
        <Route path={PaymentRoutes.PAYOUT_INCOMING} element={<IncomingTransactionsPage />} />
        <Route path={PaymentRoutes.PAYOUT_DETAILS} element={<PayoutDetailPage />} />
        <Route path={PaymentRoutes.PAYMENT_LINKS} element={<PaymentLinksListPage />} />

        <Route path={PaymentRoutes.TRANSACTIONS} element={<TransactionListPage />} />
        <Route path={PaymentRoutes.SETTINGS_CONFIG} element={<MyConfigurationPage />} />
        <Route
          path={PaymentRoutes.SETTINGS_INTEGRATION}
          element={<PaymentsIntegrationPage />}
        />
        <Route path={PaymentRoutes.SETTINGS_USERS} element={<UserManagementPage />} />
        <Route
          path={PaymentRoutes.ADD_PAYMENT_METHOD_MANDATE}
          element={<AddPaymentMethodMandatePage />}
        />
        <Route path={PaymentRoutes.CHECKOUT_INVOICE_PAY} element={<CheckoutInvoicePage />} />
        <Route path={PaymentRoutes.CHECKOUT_PAYMENT_LINK} element={<PaymentLinkCheckoutPage />} />
        <Route path={PaymentRoutes.INVOICE_RECEIPT} element={<InvoiceReceiptPage />} />
        <Route path={PaymentRoutes.REFUND_RECEIPT} element={<RefundReceiptPage />} />
        <Route path={PaymentRoutes.CLIENT_PORTAL_LOGIN} element={<ClientPortalLoginPage />} />
        <Route
          path={PaymentRoutes.CLIENT_PORTAL_INVOICES}
          element={<ClientPortalInvoiceListPage />}
        />
        <Route
          path={PaymentRoutes.CLIENT_PORTAL_CUSTOMERS}
          element={<ClientPortalCustomerListPage />}
        />
        <Route
          path={PaymentRoutes.CLIENT_PORTAL_CUSTOMERS_DETAILS}
          element={<ClientPortalCustomerDetailPage />}
        />
        <Route
          path={PaymentRoutes.INVOICE_AUTO_COLLECT_DETAIL}
          element={<InvoiceAutoCollectDetailsPage />}
        />
        <Route path={PaymentRoutes.ONBOARDING_FORM} element={<OnboardingStartForm />} />
        <Route
          path={PaymentRoutes.ZOMENTUM_GROW_QUOTE_PAY}
          element={<ZomentumGrowQuotePayPage />}
        />
        <Route path={PaymentRoutes.QUICKBOOKS_SETTINGS} element={<QuickbooksSettingsPage />} />
        <Route path={PaymentRoutes.XERO_SETTINGS} element={<XeroSettingsPage />} />
        <Route path={PaymentRoutes.REGISTER} element={<RegisterPage />} />
        <Route path={PaymentRoutes.ACCEPT_USER_INVITE} element={<AcceptUserInvitePage />} />
        {currentUser?.is_admin && (
          <>
            <Route path={PaymentRoutes.ADMIN_TEAMS} element={<AdminTeamListPage />} />
            <Route path={PaymentRoutes.ADMIN_TEAM_DETAIL} element={<AdminTeamDetailPage />} />
            <Route
              path={PaymentRoutes.ADMIN_TEAM_TRANSACTION_LIST}
              element={<AdminTeamTransactionsListPage />}
            />
            <Route
              path={PaymentRoutes.ADMIN_TEAM_TRANSFERS_LIST}
              element={<AdminTeamTransfersListPage />}
            />
            <Route
              path={PaymentRoutes.PAYOUT_TROUBLESHOOT}
              element={<PayoutTroubleshootPage />}
            />
            <Route
              path={PaymentRoutes.ADMIN_TEAM_USER_LIST}
              element={<AdminTeamUserListPage />}
            />
            <Route
              path={PaymentRoutes.ADMIN_TEAM_PAYOUTS_LIST}
              element={<AdminTeamPayoutsListPage />}
            />
            <Route
              path={PaymentRoutes.ADMIN_TEAM_PAYOUT_TROUBLESHOOT}
              element={<AdminTeamPayoutTroubleshootPage />}
            />
          </>
        )}
        <Route
          path="*"
          element={
            <Navigate
              to={onboardingComplete ? PaymentRoutes.CUSTOMERS : PaymentRoutes.GETTING_STARTED}
              replace={true}
            />
          }
        />
      </Routes>
    </Suspense>
    </>
  );
};

export default PaymentDashboardRoutes;
